import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { AdministratorInCamelCase } from 'mbs-ui-shared';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import Computer, { AgentType, ComputersResponse } from 'mbs-ui-shared@models/Computer';
import { Subject, interval } from 'rxjs';
import { filter, map, scan, switchMap, takeUntil } from 'rxjs/operators';
import { InstallationView } from 'wizards/steps/installation/installation.constants';
import { getBuildsSelectionConfig } from 'wizards/try-rmm-for-providers/installation-step/installation-step.constants';

@UntilDestroy()
@Component({
  selector: 'mbs-installation-step',
  templateUrl: './installation-step.component.html',
  styleUrls: ['./installation-step.component.scss']
})
export class InstallationStepComponent {
  @Input() wizardType: string;
  @Input() autoInstall: boolean;
  @Input() selectedAdminList: AdministratorInCamelCase[];
  @Input() selectedComputerList: Computer[];
  @Input() isCurrent: boolean;
  @Input() buildGenerationLaunched = false;
  @Output() forceNextStep = new EventEmitter();
  @Output() setComputerWithInstalledRMM = new EventEmitter<Computer>();

  public readonly buildsSelectionConfig = getBuildsSelectionConfig(this.i18next);
  public readonly installationView = InstallationView.Rmm;
  public copied = false;

  public installationTitle: string;
  public installationDescription: string;
  public showInstallError = false;
  private isInstalled = false;
  private pickedComputer: Computer;
  private rmmAgentInitialisationAverageTime = 9000;
  private poolingDelay = 5000;
  private pollingLimit = 150;
  private stopComputerPoll$ = new Subject<boolean>();

  constructor(
    private i18next: I18NextPipe,
    private computersFacade: ComputersFacade, // install verification
    private facade: ComputersFacade
  ) {}

  ngOnInit(): void {
    this.getTexts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.buildGenerationLaunched && this.buildGenerationLaunched === true) {
      this.autoInstall && this.automaticInstall();
    }
  }

  // automatic
  public automaticInstall(): void {
    (this.selectedComputerList || [])
      .filter((computer) => computer.online)
      .forEach((computer) => {
        this.facade
          .installAgent(computer, AgentType.RMM)
          .pipe(untilDestroyed(this))
          .subscribe((response) => {
            if (response.isErrorsInResultList === true) {
              response.ok === true
                ? (this.installationDescription = this.i18next.transform('wizards:tryRmm:automatic_install_description_issue'))
                : (this.installationDescription = this.i18next.transform('wizards:tryRmm:automatic_install_description_error'));
              this.showInstallError = true;
              this.autoInstall = false;
            } else {
              this.installationCheck();
            }
          });
      });

    if (this.autoInstall && !this.selectedComputerList.some((computer) => computer.online)) {
      this.installationDescription = this.i18next.transform('wizards:tryRmm:automatic_install_description_error');
      this.showInstallError = true;
      this.autoInstall = false;
    }
  }

  public onManualDownloadCompleted(computer: Computer): void {
    this.setComputerWithInstalledRMM.emit(computer);
    this.forceNextStep.emit(true);
  }

  private getTexts(): void {
    this.installationTitle = this.i18next.transform('wizards:tryRmm:automatic_install_title', { format: 'title' });
    this.installationDescription = this.i18next.transform('wizards:tryRmm:automatic_install_description');
  }

  private installationCheck(): void {
    interval(this.poolingDelay)
      .pipe(
        takeUntil(this.stopComputerPoll$.asObservable()),
        switchMap(() =>
          this.computersFacade.getComputers({
            limit: 1,
            offset: 0,
            online: true,
            appIds: [AgentType.RMM]
          })
        ),
        scan((acc, response) => ({ acc: acc.acc + 1, response }), { acc: 0, response: undefined as ComputersResponse }),
        map(({ acc, response }) => {
          if (acc >= this.pollingLimit || response?.total) this.stopComputerPoll$.next(true);
          return response;
        }),
        filter((response) => !!response.data.length),
        untilDestroyed(this)
      )
      .subscribe({
        next: (response) => {
          this.pickedComputer = response.data[0];
          this.isInstalled = true;
          this.installationStepFinalSetup();
        },
        error: () => this.stopComputerPoll$.next(true)
      });
  }

  // additional waiting time should be made for RMM Agent initialisation, before the next step
  private installationStepFinalSetup() {
    setTimeout(() => {
      this.setComputerWithInstalledRMM.emit(this.pickedComputer);
      this.forceNextStep.emit();
    }, this.rmmAgentInitialisationAverageTime);
  }
}
